import { Injectable } from 'angular-ts-decorators';

import { defer, Observable } from 'rxjs';
import { Domain } from './domain';
import type { IConfig } from './config';

/**
 * Service in the sharedModule. It's supposed to be used only by {@link
 * sharedModule.session session} service. It's separated mainly due to
 * simplify refactoring, later should be merged with session.
 * this service is only used for the low level request without business logic
 */
@Injectable('sessionRequests')
export class SessionRequests {
  constructor(
    private $http: ng.IHttpService,
    private $q: ng.IQService,
    private domain: Domain,
    private config: IConfig,
  ) {
    'ngInject';
  }

  private baseUrl(): string {
    return '//' + this.domain.sibling(this.config.spynlSubdomain);
  }

  /**
   * Access information about current user.
   *
   * @returns {Observable}
   * On success emit data in the format:
   * <pre>
   * {
   *   username: <string>, # User name & maine id
   *   fullname: <string>, # User full name
   *   email: <string>, # User email, often dummy email ending with '.nu'
   *   active: <boolean>, # information if the user was activated
   *   current_tenant: <string>, # tenant id of the current session
   *   _id: <string>, # user id
   *   tenants: Array<object> [{
   *     id: <string>, # tenant id
   *     name: <string> # tenant name
   *   }],
   *   tz: <string> # time zone information
   * }
   * </pre>
   */
  getCurrent(): Observable<any> {
    return defer(() =>
      this.$http
        .get(this.baseUrl() + '/me', {
          // done on config level for v7; setting for v6
          withCredentials: true,
        })
        .then((response: any) => response.data.data),
    );
  }

  /**
   *
   * Send the login requests with provided credentials. To properly start a
   * new session, please use {@link sharedModule.sessionManager#login
   * sessionManager.login}
   *
   * @param {string} username
   * user id usually email
   * @param {string} password
   * password
   * @param {boolean} rememberMe
   * Boolean that indicates if user whats to be logged in longer then default 48h.
   * @returns {httpPromise} it will resolve with object provided by backend,
   * or reject with error description. Example resolve:
   * <pre>
   * {
   *   "status": "ok", # status, all different then ok, is sign of error
   *   "current_tenant": "91537", # tenant id
   *   "default_application": "dashboard",
   *   "tenants": [
   *     {
   *       "id": "91537",
   *       "name": "maddoxx test"
   *     }
   *   ],
   *   "sid": "a5aae9519d5a485ea7e06304ee2b02e7" # session id, added to the
   *                                             # cookie on the server side
   * }
   * </pre>
   */
  login(
    username: string,
    password: string,
    rememberMe: boolean,
  ): ng.IPromise<any> {
    return this.$http
      .post(this.baseUrl() + '/login', {
        username,
        password,
        remember_me: !!rememberMe,
      })
      .then(
        (response) => response.data,
        // TODO CORE-440 move error to catch, and remove dummy then
        (error) => {
          if (error.data && error.data.message) {
            return this.$q.reject(error.data.message);
          } else {
            return this.$q.reject(error);
          }
        },
      );
  }

  loginTwoFa(token: string, code: string): ng.IPromise<any> {
    return this.$http
      .post(this.baseUrl() + '/validate-otp', {
        '2FAToken': token,
        '2FAOtp': code,
      })
      .then(
        (response) => response.data,
        (error) => {
          if (error.data && error.data.message) {
            return this.$q.reject(error.data.message);
          } else {
            return this.$q.reject(error);
          }
        },
      );
  }

  /**
   *
   * Try logout the current user.
   *
   * @param {string} sid
   * Session id provided if the cookie is already deleted
   *
   * @returns {httpPromise} it will resolve empty, or fail with error description
   */
  logout(sid?: string): ng.IPromise<any> {
    return this.$http.post(this.baseUrl() + '/logout', { sid }).then(
      (data) => data,
      // TODO CORE-440 move error to catch, and remove dummy then
      (error) => this.$q.reject(error.data.message),
    );
  } /**
   *
   * Try to set the tenantId as tenant for currently logged in user. Use
   * {@link sharedModule.session#setTenant user.current} instead to update
   * session information.
   *
   * @param {string} tenantId
   * Current user need to have a permission to work as the tenant.
   * @returns {httpPromise} it will resolve with /set-tenant response, or reject with
   * error
   */
  setTenant(tenantId: string): ng.IPromise<any> {
    return this.$http
      .post(
        this.baseUrl() + '/set-tenant',
        { id: tenantId },
        {
          withCredentials: true,
        },
      )
      .then(
        (response) => response.data,
        (error) => {
          if (angular.isObject(error) && error.data) {
            return this.$q.reject(error.data.message);
          }
          return this.$q.reject(error);
        },
      );
  }
}
